import { jsPDF } from "jspdf";
import type { AandLFormData } from "../AandLForm";

// A4 dimensions in mm
const PAGE_W = 210;
const MARGIN_X = 15;
const MARGIN_TOP = 10;
const USABLE_W = PAGE_W - 2 * MARGIN_X; // 180mm

// Colors
const HEADING_BG = [20, 28, 73]; // brand #141C49 — Master Navy
const DIVIDER_COLOR = [200, 200, 200];
const FIELD_BG = [255, 255, 255]; // brand form fieldBg: white
const FIELD_BORDER = [180, 180, 180];

// Field sizing
const FIELD_H = 5;
const LABEL_FONT_SIZE = 7;
const FIELD_FONT_SIZE = 8;
const LABEL_OFFSET = 3;
const ROW_ADVANCE = LABEL_OFFSET + FIELD_H + 2; // 10mm

let cursorY = MARGIN_TOP;

function resetCursor() {
  cursorY = MARGIN_TOP;
}

function advanceCursor(amount: number) {
  cursorY += amount;
}

function drawSectionHeading(doc: jsPDF, text: string) {
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.rect(MARGIN_X, cursorY, USABLE_W, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(text, MARGIN_X + 2, cursorY + 3.5);
  doc.setTextColor(0, 0, 0);
  advanceCursor(6);
}

function drawDivider(doc: jsPDF) {
  doc.setDrawColor(DIVIDER_COLOR[0], DIVIDER_COLOR[1], DIVIDER_COLOR[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X, cursorY, MARGIN_X + USABLE_W, cursorY);
  advanceCursor(2);
}

function addLabeledTextField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 60);
  doc.text(label, x, y - 1);
  doc.setTextColor(0, 0, 0);

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addInlineTextField(
  doc: jsPDF,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addCheckboxField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  checked: boolean,
  x: number,
  y: number
) {
  const cbSize = 3.5;

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, cbSize, cbSize, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cb = new (doc as any).AcroFormCheckBox();
  cb.fieldName = fieldName;
  cb.x = x;
  cb.y = y;
  cb.width = cbSize;
  cb.height = cbSize;
  if (checked) {
    cb.appearanceState = "On";
    cb.value = "On";
    cb.defaultValue = "On";
  } else {
    cb.appearanceState = "Off";
    cb.value = "Off";
    cb.defaultValue = "Off";
  }
  doc.addField(cb);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "normal");
  doc.text(label, x + cbSize + 1.5, y + 2.8);
}

async function loadLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch("/assets/One-logo.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateAandLPDF(formData: AandLFormData): Promise<void> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  resetCursor();

  const logoBase64 = await loadLogoAsBase64();

  const gap = 3;
  const lo = LABEL_OFFSET;

  // ==================== HEADER ====================
  if (logoBase64) {
    const logoW = 15;
    const logoH = logoW * (330 / 294);
    doc.addImage(logoBase64, "PNG", MARGIN_X, cursorY, logoW, logoH);
  }

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("ASSETS & LIABILITIES", PAGE_W / 2, cursorY + 8, { align: "center" });

  doc.setFontSize(5.5);
  doc.setFont("helvetica", "bold");
  const rightX = MARGIN_X + USABLE_W;
  doc.text("One Property Australia", rightX, cursorY + 2.5, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text("ACN: 664 898 846", rightX, cursorY + 5, { align: "right" });
  doc.text("6/22 Alexandra Pl,", rightX, cursorY + 7.5, { align: "right" });
  doc.text("Murarrie QLD 4172", rightX, cursorY + 10, { align: "right" });
  doc.text("ONEPROPERTYAUSTRALIA.COM.AU", rightX, cursorY + 12.5, { align: "right" });

  advanceCursor(18);
  drawDivider(doc);

  // ==================== APPLICANT DETAILS (2 COLUMNS) ====================
  drawSectionHeading(doc, "APPLICANT DETAILS");

  const colGap = 6;
  const colW = (USABLE_W - colGap) / 2; // ~87mm
  const colAx = MARGIN_X;
  const colBx = MARGIN_X + colW + colGap;

  // Column sub-labels
  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Applicant A", colAx, cursorY + 3);
  doc.text("Applicant B", colBx, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  // Name
  addLabeledTextField(doc, "Name:", "nameA", formData.nameA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Name:", "nameB", formData.nameB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Address
  addLabeledTextField(doc, "Address:", "addressA", formData.addressA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Address:", "addressB", formData.addressB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Residence / State / Postcode (3 sub-cols per column)
  const sub3 = (colW - 2 * 2) / 3; // 2mm gaps between 3 sub-cols
  addLabeledTextField(doc, "Residence (Yrs):", "residenceYearsA", formData.residenceYearsA, colAx, cursorY + lo, sub3);
  addLabeledTextField(doc, "State:", "stateA", formData.stateA, colAx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "P/Code:", "postcodeA", formData.postcodeA, colAx + 2 * (sub3 + 2), cursorY + lo, sub3);
  addLabeledTextField(doc, "Residence (Yrs):", "residenceYearsB", formData.residenceYearsB, colBx, cursorY + lo, sub3);
  addLabeledTextField(doc, "State:", "stateB", formData.stateB, colBx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "P/Code:", "postcodeB", formData.postcodeB, colBx + 2 * (sub3 + 2), cursorY + lo, sub3);
  advanceCursor(ROW_ADVANCE);

  // Ph(H) / Mobile / DOB
  addLabeledTextField(doc, "Ph (H):", "phoneHomeA", formData.phoneHomeA, colAx, cursorY + lo, sub3);
  addLabeledTextField(doc, "Mobile:", "mobileA", formData.mobileA, colAx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "D.O.B:", "dobA", formData.dobA, colAx + 2 * (sub3 + 2), cursorY + lo, sub3);
  addLabeledTextField(doc, "Ph (H):", "phoneHomeB", formData.phoneHomeB, colBx, cursorY + lo, sub3);
  addLabeledTextField(doc, "Mobile:", "mobileB", formData.mobileB, colBx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "D.O.B:", "dobB", formData.dobB, colBx + 2 * (sub3 + 2), cursorY + lo, sub3);
  advanceCursor(ROW_ADVANCE);

  // Occupation
  addLabeledTextField(doc, "Occupation:", "occupationA", formData.occupationA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Occupation:", "occupationB", formData.occupationB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Employer
  addLabeledTextField(doc, "Employer:", "employerA", formData.employerA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Employer:", "employerB", formData.employerB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Employment type checkboxes
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Employment:", colAx, cursorY + 3.5);
  addCheckboxField(doc, "F/T", "empFITA", formData.empFITA, colAx + 22, cursorY);
  addCheckboxField(doc, "P/T", "empPTA", formData.empPTA, colAx + 37, cursorY);
  addCheckboxField(doc, "Casual", "empCasualA", formData.empCasualA, colAx + 52, cursorY);
  doc.text("Employment:", colBx, cursorY + 3.5);
  addCheckboxField(doc, "F/T", "empFITB", formData.empFITB, colBx + 22, cursorY);
  addCheckboxField(doc, "P/T", "empPTB", formData.empPTB, colBx + 37, cursorY);
  addCheckboxField(doc, "Casual", "empCasualB", formData.empCasualB, colBx + 52, cursorY);
  advanceCursor(7);

  // Taxable Income
  addLabeledTextField(doc, "Taxable Income P/A $:", "taxableIncomeA", formData.taxableIncomeA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Taxable Income P/A $:", "taxableIncomeB", formData.taxableIncomeB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Dependents / Ages
  const depW = (colW - gap) / 2;
  addLabeledTextField(doc, "Dependents:", "dependentsA", formData.dependentsA, colAx, cursorY + lo, depW);
  addLabeledTextField(doc, "Ages:", "agesA", formData.agesA, colAx + depW + gap, cursorY + lo, depW);
  addLabeledTextField(doc, "Dependents:", "dependentsB", formData.dependentsB, colBx, cursorY + lo, depW);
  addLabeledTextField(doc, "Ages:", "agesB", formData.agesB, colBx + depW + gap, cursorY + lo, depW);
  advanceCursor(ROW_ADVANCE);

  drawDivider(doc);

  // ==================== SELF EMPLOYED & INCOME ====================
  drawSectionHeading(doc, "SELF EMPLOYED & INCOME");

  // Self Employed + Annual Gross Turnover
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Self Employed:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "selfEmployedYes", formData.selfEmployedYes, MARGIN_X + 26, cursorY);
  addCheckboxField(doc, "NO", "selfEmployedNo", formData.selfEmployedNo, MARGIN_X + 41, cursorY);
  addLabeledTextField(doc, "Annual Gross Turnover $:", "grossTurnover", formData.grossTurnover, MARGIN_X + 60, cursorY + lo, USABLE_W - 60);
  advanceCursor(ROW_ADVANCE);

  // Tax Returns + Trading Name
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Last 2yrs Tax Returns:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "taxReturnsYes", formData.taxReturnsYes, MARGIN_X + 38, cursorY);
  addCheckboxField(doc, "NO", "taxReturnsNo", formData.taxReturnsNo, MARGIN_X + 53, cursorY);
  addLabeledTextField(doc, "Trading Name:", "tradingName", formData.tradingName, MARGIN_X + 70, cursorY + lo, USABLE_W - 70);
  advanceCursor(ROW_ADVANCE);

  // Net Taxable Income + Income Split
  const halfW = (USABLE_W - gap) / 2;
  addLabeledTextField(doc, "Net Taxable Income p.a $:", "netTaxableIncome", formData.netTaxableIncome, MARGIN_X, cursorY + lo, halfW);
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Income Split:", MARGIN_X + halfW + gap, cursorY + 3.5);
  addCheckboxField(doc, "YES", "incomeSplitYes", formData.incomeSplitYes, MARGIN_X + halfW + gap + 22, cursorY);
  addCheckboxField(doc, "NO", "incomeSplitNo", formData.incomeSplitNo, MARGIN_X + halfW + gap + 37, cursorY);
  addLabeledTextField(doc, "Split Amount $:", "incomeSplitAmount", formData.incomeSplitAmount, MARGIN_X + halfW + gap + 55, cursorY + lo, halfW - gap - 55);
  advanceCursor(ROW_ADVANCE);

  drawDivider(doc);

  // ==================== ASSETS ====================
  drawSectionHeading(doc, "ASSETS");

  // Asset table column widths
  const aLabelW = 36;
  const aGap = 2;
  const aValueW = 33;
  const aOwnerW = 65;
  const aRentW = USABLE_W - aLabelW - aValueW - aOwnerW - 3 * aGap;

  const ax0 = MARGIN_X;
  const ax1 = ax0 + aLabelW + aGap;
  const ax2 = ax1 + aValueW + aGap;
  const ax3 = ax2 + aOwnerW + aGap;

  // Asset column headers
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.setTextColor(255, 255, 255);
  doc.rect(ax1, cursorY, aValueW, 4.5, "F");
  doc.text("Value $", ax1 + aValueW / 2, cursorY + 3, { align: "center" });
  doc.rect(ax2, cursorY, aOwnerW, 4.5, "F");
  doc.text("Owner / Title", ax2 + aOwnerW / 2, cursorY + 3, { align: "center" });
  doc.rect(ax3, cursorY, aRentW, 4.5, "F");
  doc.text("Rent p.w. $", ax3 + aRentW / 2, cursorY + 3, { align: "center" });
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  const assetRows: { label: string; vf: string; vv: string; of: string; ov: string; rf: string; rv: string }[] = [
    { label: "House", vf: "assetHouseValue", vv: formData.assetHouseValue, of: "assetHouseOwner", ov: formData.assetHouseOwner, rf: "assetHouseRent", rv: formData.assetHouseRent },
    { label: "Property 1", vf: "assetProp1Value", vv: formData.assetProp1Value, of: "assetProp1Owner", ov: formData.assetProp1Owner, rf: "assetProp1Rent", rv: formData.assetProp1Rent },
    { label: "Property 2", vf: "assetProp2Value", vv: formData.assetProp2Value, of: "assetProp2Owner", ov: formData.assetProp2Owner, rf: "assetProp2Rent", rv: formData.assetProp2Rent },
    { label: "Property 3", vf: "assetProp3Value", vv: formData.assetProp3Value, of: "assetProp3Owner", ov: formData.assetProp3Owner, rf: "assetProp3Rent", rv: formData.assetProp3Rent },
    { label: "Other Prop. 1", vf: "assetOtherProp1Value", vv: formData.assetOtherProp1Value, of: "assetOtherProp1Owner", ov: formData.assetOtherProp1Owner, rf: "assetOtherProp1Rent", rv: formData.assetOtherProp1Rent },
    { label: "Other Prop. 2", vf: "assetOtherProp2Value", vv: formData.assetOtherProp2Value, of: "assetOtherProp2Owner", ov: formData.assetOtherProp2Owner, rf: "assetOtherProp2Rent", rv: formData.assetOtherProp2Rent },
    { label: "Other Prop. 3", vf: "assetOtherProp3Value", vv: formData.assetOtherProp3Value, of: "assetOtherProp3Owner", ov: formData.assetOtherProp3Owner, rf: "assetOtherProp3Rent", rv: formData.assetOtherProp3Rent },
    { label: "Superannuation", vf: "assetSuperValue", vv: formData.assetSuperValue, of: "assetSuperOwner", ov: formData.assetSuperOwner, rf: "assetSuperRent", rv: "" },
    { label: "Shares", vf: "assetSharesValue", vv: formData.assetSharesValue, of: "assetSharesOwner", ov: formData.assetSharesOwner, rf: "assetSharesRent", rv: "" },
    { label: "Savings", vf: "assetSavingsValue", vv: formData.assetSavingsValue, of: "assetSavingsOwner", ov: formData.assetSavingsOwner, rf: "assetSavingsRent", rv: "" },
    { label: "Other", vf: "assetOther1Value", vv: formData.assetOther1Value, of: "assetOther1Owner", ov: formData.assetOther1Owner, rf: "assetOther1Rent", rv: "" },
    { label: "Other", vf: "assetOther2Value", vv: formData.assetOther2Value, of: "assetOther2Owner", ov: formData.assetOther2Owner, rf: "assetOther2Rent", rv: "" },
  ];

  for (const row of assetRows) {
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 50);
    doc.text(row.label, ax0 + 1, cursorY + 3.5);
    addInlineTextField(doc, row.vf, row.vv, ax1, cursorY, aValueW);
    addInlineTextField(doc, row.of, row.ov, ax2, cursorY, aOwnerW);
    addInlineTextField(doc, row.rf, row.rv, ax3, cursorY, aRentW);
    advanceCursor(FIELD_H + 1.5);
  }

  // ==================== PAGE 2 ====================
  doc.addPage();
  resetCursor();

  // Page 2 compact header
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("ASSETS & LIABILITIES (continued)", PAGE_W / 2, cursorY + 5, { align: "center" });
  doc.setTextColor(0, 0, 0);
  advanceCursor(10);

  // ==================== LIABILITIES ====================
  drawSectionHeading(doc, "LIABILITIES");

  // Liability table column widths
  const lLabelW = 30;
  const lGap = 2;
  const lLenderW = 45;
  const lBalW = 25;
  const lLimitW = 30;
  const lRepayW = USABLE_W - lLabelW - lLenderW - lBalW - lLimitW - 4 * lGap;

  const lx0 = MARGIN_X;
  const lx1 = lx0 + lLabelW + lGap;
  const lx2 = lx1 + lLenderW + lGap;
  const lx3 = lx2 + lBalW + lGap;
  const lx4 = lx3 + lLimitW + lGap;

  // Liability column headers
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.setTextColor(255, 255, 255);
  doc.rect(lx1, cursorY, lLenderW, 4.5, "F");
  doc.text("Lender", lx1 + lLenderW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx2, cursorY, lBalW, 4.5, "F");
  doc.text("Balance $", lx2 + lBalW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx3, cursorY, lLimitW, 4.5, "F");
  doc.text("Limit Mo. $", lx3 + lLimitW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx4, cursorY, lRepayW, 4.5, "F");
  doc.text("Repayment $", lx4 + lRepayW / 2, cursorY + 3, { align: "center" });
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  const liabRows: { label: string; lf: string; lv: string; bf: string; bv: string; lif: string; liv: string; rf: string; rv: string }[] = [
    { label: "House", lf: "liabHouseLender", lv: formData.liabHouseLender, bf: "liabHouseBalance", bv: formData.liabHouseBalance, lif: "liabHouseLimit", liv: formData.liabHouseLimit, rf: "liabHouseRepayment", rv: formData.liabHouseRepayment },
    { label: "Property 1", lf: "liabProp1Lender", lv: formData.liabProp1Lender, bf: "liabProp1Balance", bv: formData.liabProp1Balance, lif: "liabProp1Limit", liv: formData.liabProp1Limit, rf: "liabProp1Repayment", rv: formData.liabProp1Repayment },
    { label: "Property 2", lf: "liabProp2Lender", lv: formData.liabProp2Lender, bf: "liabProp2Balance", bv: formData.liabProp2Balance, lif: "liabProp2Limit", liv: formData.liabProp2Limit, rf: "liabProp2Repayment", rv: formData.liabProp2Repayment },
    { label: "Property 3", lf: "liabProp3Lender", lv: formData.liabProp3Lender, bf: "liabProp3Balance", bv: formData.liabProp3Balance, lif: "liabProp3Limit", liv: formData.liabProp3Limit, rf: "liabProp3Repayment", rv: formData.liabProp3Repayment },
    { label: "Personal Loans", lf: "liabPersonalLender", lv: formData.liabPersonalLender, bf: "liabPersonalBalance", bv: formData.liabPersonalBalance, lif: "liabPersonalLimit", liv: formData.liabPersonalLimit, rf: "liabPersonalRepayment", rv: formData.liabPersonalRepayment },
    { label: "Credit Card 1", lf: "liabCC1Lender", lv: formData.liabCC1Lender, bf: "liabCC1Balance", bv: formData.liabCC1Balance, lif: "liabCC1Limit", liv: formData.liabCC1Limit, rf: "liabCC1Repayment", rv: formData.liabCC1Repayment },
    { label: "Credit Card 2", lf: "liabCC2Lender", lv: formData.liabCC2Lender, bf: "liabCC2Balance", bv: formData.liabCC2Balance, lif: "liabCC2Limit", liv: formData.liabCC2Limit, rf: "liabCC2Repayment", rv: formData.liabCC2Repayment },
    { label: "HP / Lease", lf: "liabHPLender", lv: formData.liabHPLender, bf: "liabHPBalance", bv: formData.liabHPBalance, lif: "liabHPLimit", liv: formData.liabHPLimit, rf: "liabHPRepayment", rv: formData.liabHPRepayment },
    { label: "Overdraft", lf: "liabOverdraftLender", lv: formData.liabOverdraftLender, bf: "liabOverdraftBalance", bv: formData.liabOverdraftBalance, lif: "liabOverdraftLimit", liv: formData.liabOverdraftLimit, rf: "liabOverdraftRepayment", rv: formData.liabOverdraftRepayment },
    { label: "Priv. Health Ins.", lf: "liabPrivateHealthLender", lv: formData.liabPrivateHealthLender, bf: "liabPrivateHealthBalance", bv: formData.liabPrivateHealthBalance, lif: "liabPrivateHealthLimit", liv: formData.liabPrivateHealthLimit, rf: "liabPrivateHealthRepayment", rv: formData.liabPrivateHealthRepayment },
    { label: "Priv. School Fees", lf: "liabPrivateSchoolLender", lv: formData.liabPrivateSchoolLender, bf: "liabPrivateSchoolBalance", bv: formData.liabPrivateSchoolBalance, lif: "liabPrivateSchoolLimit", liv: formData.liabPrivateSchoolLimit, rf: "liabPrivateSchoolRepayment", rv: formData.liabPrivateSchoolRepayment },
    { label: "Child Care Exp.", lf: "liabChildCareLender", lv: formData.liabChildCareLender, bf: "liabChildCareBalance", bv: formData.liabChildCareBalance, lif: "liabChildCareLimit", liv: formData.liabChildCareLimit, rf: "liabChildCareRepayment", rv: formData.liabChildCareRepayment },
    { label: "Other", lf: "liabOther1Lender", lv: formData.liabOther1Lender, bf: "liabOther1Balance", bv: formData.liabOther1Balance, lif: "liabOther1Limit", liv: formData.liabOther1Limit, rf: "liabOther1Repayment", rv: formData.liabOther1Repayment },
    { label: "Other", lf: "liabOther2Lender", lv: formData.liabOther2Lender, bf: "liabOther2Balance", bv: formData.liabOther2Balance, lif: "liabOther2Limit", liv: formData.liabOther2Limit, rf: "liabOther2Repayment", rv: formData.liabOther2Repayment },
    { label: "Other", lf: "liabOther3Lender", lv: formData.liabOther3Lender, bf: "liabOther3Balance", bv: formData.liabOther3Balance, lif: "liabOther3Limit", liv: formData.liabOther3Limit, rf: "liabOther3Repayment", rv: formData.liabOther3Repayment },
  ];

  for (const row of liabRows) {
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 50);
    doc.text(row.label, lx0 + 1, cursorY + 3.5);
    addInlineTextField(doc, row.lf, row.lv, lx1, cursorY, lLenderW);
    addInlineTextField(doc, row.bf, row.bv, lx2, cursorY, lBalW);
    addInlineTextField(doc, row.lif, row.liv, lx3, cursorY, lLimitW);
    addInlineTextField(doc, row.rf, row.rv, lx4, cursorY, lRepayW);
    advanceCursor(FIELD_H + 1.5);
  }

  advanceCursor(3);
  drawDivider(doc);

  // ==================== PRIVACY ACT ====================
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("PRIVACY ACT", MARGIN_X, cursorY + 5);
  doc.setTextColor(0, 0, 0);
  advanceCursor(7);

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  const privacyText =
    "I/We authorise release of the information contained herein to such parties as are necessary to assess and provide advice regarding property investment provided that such information is treated as confidential and restricted to such parties as are affiliated with One Property Australia.";
  const privacyLines = doc.splitTextToSize(privacyText, USABLE_W);
  doc.text(privacyLines, MARGIN_X, cursorY + 4);
  advanceCursor(14);

  drawDivider(doc);

  // ==================== SIGNATURES ====================
  const sigColW = (USABLE_W - colGap) / 2;
  const sigH = 16;

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Signature (Applicant A):", MARGIN_X, cursorY + 3);
  doc.text("Signature (Applicant B):", MARGIN_X + sigColW + colGap, cursorY + 3);
  advanceCursor(5);

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN_X, cursorY, sigColW, sigH, "FD");
  doc.rect(MARGIN_X + sigColW + colGap, cursorY, sigColW, sigH, "FD");
  advanceCursor(sigH + 4);

  // Consultant Name + Date
  addLabeledTextField(doc, "Consultant Name:", "consultantName", formData.consultantName, MARGIN_X, cursorY + lo, sigColW);
  addLabeledTextField(doc, "Date:", "date", formData.date, MARGIN_X + sigColW + colGap, cursorY + lo, sigColW);

  doc.save("OPA_Assets_and_Liabilities.pdf");
}
